import { App, Form } from 'antd';
import bgHome from 'assets/images/bg_home.png';
import HistoryPredictions from 'components/HistoryPredictions';
import HeaderMenu from 'components/Header/HeaderMenu';
import HeroCountdown from 'components/Home/HeroCountdown';
import HistorySearchBar from 'components/Home/HistorySearchBar';
import LeagueSelectorBar from 'components/Home/LeagueSelectorBar';
import MatchFilterTabs from 'components/Home/MatchFilterTabs';
import PredictionHistoryTable from 'components/HistoryPredictions/PredictionHistoryTable';
import ResultPredictionBoard from 'components/Home/ResultPredictionBoard';
import ResultRulePrizePanel from 'components/Home/ResultRulePrizePanel';
import SchedulePopup from 'components/Home/SchedulePopup';
import ScorePredictionBoard from 'components/Home/ScorePredictionBoard';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  createPredictions,
  getAllSetting,
  getFP,
  getIP,
  getMatchList,
  getTime
} from 'service/base';
import { CONST_SITE } from 'utils/common/const';
import {
  bgTime,
  homeBanner,
  leagueDisplayOrder,
  leagues,
  titleBg
} from 'components/Home/homeConstants';

const TIMEOUT_DURATION = 10000;

const QQ88Page = () => {
  const { notification } = App.useApp();
  const location = useLocation();
  const [form] = Form.useForm();
  const watchedAccount = Form.useWatch('account', form);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [listDataMatch, setListDataMatch] = useState([]);
  const [timeServer, setTimeServer] = useState(null);
  const [liveNow, setLiveNow] = useState(dayjs());
  const [ip, setIp] = useState(null);
  const [fp, setFp] = useState(null);

  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [activePredictMode, setActivePredictMode] = useState('score');
  const [activeHomeView, setActiveHomeView] = useState(
    location.pathname === '/history' ? 'history' : 'predict'
  );
  const [activeHistoryTab, setActiveHistoryTab] = useState('worldcup');
  const [resultActiveTabIndex, setResultActiveTabIndex] = useState(0);
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);

  const leagueListRef = useRef(null);
  const previousPathRef = useRef(location.pathname);
  const pendingPredictionRef = useRef(null);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;

    if (currentPath === '/history') {
      setActiveHomeView('history');
      setActiveHistoryTab('worldcup');
    } else if (previousPath === '/history' && currentPath === '/') {
      setActiveHomeView('predict');
    }

    previousPathRef.current = currentPath;
  }, [location.pathname]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        await getAllSetting({ site: CONST_SITE.QQ88 });
      } catch (error) {}

      try {
        const [ipRes, fpRes, timeRes] = await Promise.all([getIP(), getFP(), getTime()]);
        setIp(ipRes);
        setFp(fpRes);
        setTimeServer(timeRes);
      } catch (error) {}
    };

    bootstrap();
  }, []);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setIsLoading(true);
        const res = await getMatchList({ site: CONST_SITE.QQ88 });
        const dataMatch =
          res?.data?.length > 0
            ? res.data.map((item) => {
                const countdown = dayjs.unix(item.startTime);
                return { ...item, countdown: new Date(countdown) };
              })
            : [];

        setListDataMatch(dataMatch || []);
      } catch (error) {
        console.error('Error fetching match list:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatches();

    const savedAccount = localStorage.getItem('saved_account');
    if (savedAccount) {
      form.setFieldsValue({ account: savedAccount });
    }
  }, [form]);

  useEffect(() => {
    const parsed = dayjs(timeServer);
    setLiveNow(parsed.isValid() ? parsed : dayjs());
  }, [timeServer]);

  useEffect(() => {
    const timer = setInterval(() => {
      setLiveNow((prev) => dayjs(prev).add(1, 'second'));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
  const handleClientSigninUpdated = async () => {
    const pendingData = pendingPredictionRef.current;
    const username = (localStorage.getItem('client_signin_username') || '').trim();

    form.setFieldsValue({ account: username });

    if (!pendingData || !username) return;

    pendingPredictionRef.current = null;

    const nextData = {
      ...pendingData,
      account: username
    };

    // Chỉ auto submit nếu thật sự có dự đoán hợp lệ
    if (hasValidPredictionData(nextData)) {
      await onFinishForm(nextData, { skipLoginCheck: true });
    }
  };

  window.addEventListener('client-signin-updated', handleClientSigninUpdated);

  return () => {
    window.removeEventListener('client-signin-updated', handleClientSigninUpdated);
  };
}, [form]);

  const serverNow = liveNow;

  const matchesByTab = useMemo(() => {
    if (!listDataMatch || listDataMatch.length === 0) return [];

    const today = serverNow.format('YYYY-MM-DD');
    const tomorrow = serverNow.add(1, 'day').format('YYYY-MM-DD');

    if (activeTab === 'today') {
      return listDataMatch.filter(
        (match) => dayjs.unix(match.startTime).format('YYYY-MM-DD') === today
      );
    }

    if (activeTab === 'tomorrow') {
      return listDataMatch.filter(
        (match) => dayjs.unix(match.startTime).format('YYYY-MM-DD') === tomorrow
      );
    }

    return listDataMatch;
  }, [activeTab, listDataMatch, serverNow]);

  const filteredMatches = useMemo(() => {
    if (selectedLeagues.length === 0) return matchesByTab;

    return matchesByTab.filter((item) => {
      const foundLeague = leagues.find(
        (league) => league.name === item.leagueName || item.leagueName?.startsWith(league.name)
      );
      return foundLeague ? selectedLeagues.includes(foundLeague.id) : false;
    });
  }, [matchesByTab, selectedLeagues]);

  const votableMatches = useMemo(() => {
    return filteredMatches.filter((item) => dayjs(item.countdown).isAfter(serverNow));
  }, [filteredMatches, serverNow]);

  const orderedLeagues = useMemo(() => {
    return leagueDisplayOrder
      .map((id) => leagues.find((league) => league.id === id))
      .filter(Boolean);
  }, []);

  const nextUpcomingMatch = useMemo(() => {
    const upcoming = [...votableMatches].sort(
      (a, b) => dayjs(a.countdown).valueOf() - dayjs(b.countdown).valueOf()
    );

    return upcoming[0] || null;
  }, [votableMatches]);

  const sectionLeagueTitle = useMemo(() => {
    if (selectedLeagues.length > 0) {
      const selectedLeagueNames = selectedLeagues
        .map((leagueId) => leagues.find((league) => league.id === leagueId)?.name)
        .filter(Boolean);

      if (selectedLeagueNames.length > 0) {
        return selectedLeagueNames.join(' | ');
      }
    }

    return nextUpcomingMatch?.leagueName || 'FIFA World Cup qualification (UEFA).';
  }, [selectedLeagues, nextUpcomingMatch]);

  const createPredictionsWithTimeout = (predictions) => {
    return Promise.race([
      createPredictions(predictions),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), TIMEOUT_DURATION)
      )
    ]);
  };

  const groupScoresByMatchId = (input, matchInfo, accountValue) => {
    const groupedMatches = Object.entries(input).reduce((acc, [key, value]) => {
      if (key === 'account') return acc;

      const parts = key.split('_');
      if (parts.length !== 2) return acc;

      const [scoreType, matchId] = parts;
      if (!matchId || !['homeScore', 'awayScore'].includes(scoreType)) return acc;
      if (value === undefined || value === null || value === '') return acc;

      if (!acc[matchId]) {
        acc[matchId] = {
          matchId,
          account: accountValue,
          homeScore: undefined,
          awayScore: undefined
        };
      }

      if (scoreType === 'homeScore') {
        acc[matchId].homeScore = value;
      } else {
        acc[matchId].awayScore = value;
      }

      return acc;
    }, {});

    return Object.values(groupedMatches)
      .filter(
        (match) =>
          match.homeScore !== undefined &&
          match.awayScore !== undefined &&
          match.homeScore !== null &&
          match.awayScore !== null &&
          match.homeScore !== '' &&
          match.awayScore !== ''
      )
      .map(({ matchId, account, homeScore, awayScore }) => {
        const matchDetails = matchInfo.find(
          (match) => parseInt(match.matchId, 10) === parseInt(matchId, 10)
        );

        return {
          matchId,
          homeScore,
          awayScore,
          account,
          ip,
          fp,
          homeName: matchDetails ? matchDetails.homeName : 'Unknown',
          awayName: matchDetails ? matchDetails.awayName : 'Unknown',
          site: SITE
        };
      });
  };

  const hasValidPredictionData = (data) => {
  if (!data) return false;

  const grouped = {};

  Object.entries(data).forEach(([key, value]) => {
    if (key === 'account') return;
    if (!key.includes('_')) return;

    const parts = key.split('_');
    if (parts.length !== 2) return;

    const [scoreType, matchId] = parts;
    if (!['homeScore', 'awayScore'].includes(scoreType)) return;
    if (value === undefined || value === null || value === '') return;

    if (!grouped[matchId]) {
      grouped[matchId] = {};
    }

    grouped[matchId][scoreType] = value;
  });

  return Object.values(grouped).some(
    (item) =>
      item.homeScore !== undefined &&
      item.homeScore !== '' &&
      item.awayScore !== undefined &&
      item.awayScore !== ''
  );
};

  const onFinishForm = async (data, options = {}) => {
    const { skipLoginCheck = false } = options;

    try {
      if (votableMatches?.length === 0) return;

      const clientSigninUsername = (localStorage.getItem('client_signin_username') || '').trim();

      if (!clientSigninUsername && !skipLoginCheck) {
        pendingPredictionRef.current = data;
        window.dispatchEvent(new Event('open-client-signin-popup'));
        return;
      }

      const accountValue = clientSigninUsername || data?.account?.trim();

      if (!accountValue) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng đăng nhập trước khi gửi dự đoán!'
        });
        return;
      }

      const hasInvalidScores = Object.entries(data).some(([key, value]) => {
        if (key === 'account' || !key.includes('_')) return false;

        const parts = key.split('_');
        if (parts.length !== 2) return false;

        const [scoreType, matchId] = parts;
        if (!['homeScore', 'awayScore'].includes(scoreType)) return false;

        const otherScoreType = scoreType === 'homeScore' ? 'awayScore' : 'homeScore';
        const otherScoreKey = `${otherScoreType}_${matchId}`;
        const otherValue = data[otherScoreKey];

        const hasValue = value !== undefined && value !== null && value !== '';
        const hasOtherValue = otherValue !== undefined && otherValue !== null && otherValue !== '';

        return hasValue && !hasOtherValue;
      });

      if (hasInvalidScores) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập đầy đủ cả hai bên tỷ số cho mỗi trận đấu!'
        });
        return;
      }

      setIsLoadingSubmit(true);

      const resultGroup = groupScoresByMatchId(data, votableMatches, accountValue);

      if (resultGroup.length === 0) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng dự đoán ít nhất một trận đấu!'
        });
        setIsLoadingSubmit(false);
        return;
      }

      const res = await createPredictionsWithTimeout(resultGroup);

      if (res?.code === 0) {
        localStorage.setItem('saved_account', accountValue);
        form.setFieldsValue({ account: accountValue });

        notification.success({
          message: 'Thành công',
          description: 'Dự đoán tỷ số thành công!'
        });

        form.resetFields(Object.keys(form.getFieldsValue()).filter((key) => key !== 'account'));
      } else if (res.valid === false) {
        notification.error({ message: res.title_mess, description: res?.text_mess });
      } else {
        notification.error({ message: 'Lỗi', description: res?.message });
      }

      setIsLoadingSubmit(false);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const onFinishFailed = () => {
    const clientSigninUsername = (localStorage.getItem('client_signin_username') || '').trim();

    if (!clientSigninUsername) {
      pendingPredictionRef.current = form.getFieldsValue();
      window.dispatchEvent(new Event('open-client-signin-popup'));
      return;
    }

    notification.warning({
      message: 'Cảnh báo',
      description: 'Vui lòng điền đầy đủ thông tin!'
    });
  };

  const handleLeagueClick = (leagueId) => {
    setSelectedLeagues((prevSelectedLeagues) =>
      prevSelectedLeagues.includes(leagueId)
        ? prevSelectedLeagues.filter((id) => id !== leagueId)
        : [...prevSelectedLeagues, leagueId]
    );
  };

  const scrollLeft = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: -220, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: 220, behavior: 'smooth' });
    }
  };

  const formatRemainingClock = (targetTime, currentTime) => {
    const target = dayjs(targetTime);
    const now = dayjs(currentTime);

    if (target.isBefore(now)) return 'Đã kết thúc';

    const diffSeconds = target.diff(now, 'second');
    const hours = String(Math.floor(diffSeconds / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((diffSeconds % 3600) / 60)).padStart(2, '0');
    const seconds = String(diffSeconds % 60).padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  };

  const getLeagueLogo = (leagueName) => {
    const league = leagues.find(
      (item) =>
        item.name.toLowerCase() === (leagueName || '').toLowerCase() ||
        (leagueName || '').toLowerCase().startsWith(item.name.toLowerCase())
    );
    return league?.logo || null;
  };

  const historyAccount = (() => {
    const clientSigninUsername = (localStorage.getItem('client_signin_username') || '').trim();
    if (clientSigninUsername) return clientSigninUsername;

    const accountFromForm = (watchedAccount || '').trim();
    if (accountFromForm) return accountFromForm;

    const savedPredictionAccount = (localStorage.getItem('saved_account') || '').trim();
    if (savedPredictionAccount) return savedPredictionAccount;

    return '';
  })();

  return (
    <div className="w-full h-full flex flex-col">
      <img
        src={bgHome}
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover"
      />

      <div className="relative z-10 pb-5">
        <HeaderMenu
          homeView={activeHomeView}
          onHistoryClick={() => {
            setActiveHomeView('history');
            setActiveHistoryTab('worldcup');
          }}
          onPredictClick={() => setActiveHomeView('predict')}
        />

        <div className="w-full mx-auto px-2 md:px-4">
          <HeroCountdown
            bannerSrc={homeBanner}
            backgroundSrc={bgTime}
            serverNow={serverNow}
            activePredictMode={activePredictMode}
            showPredictModeSwitch={activeHomeView !== 'history'}
            onScorePredictClick={() => {
              setActiveHomeView('predict');
              setActivePredictMode('score');
            }}
            onResultPredictClick={() => {
              setActiveHomeView('predict');
              setActivePredictMode('result');
            }}
          />

          {/* <img
            src="/assets/images/match/terry1.png"
            alt="host-left"
            className="hidden lg:block fixed bottom-0 left-0 h-[830px] max-h-[70vh] w-auto object-contain object-bottom pointer-events-none -z-10"
          />
          <img
            src="/assets/images/match/terry2.png"
            alt="host-right"
            className="hidden lg:block fixed bottom-0 right-0 h-[830px] max-h-[70vh] w-auto object-contain object-bottom pointer-events-none -z-10"
          /> */}

          {activeHomeView === 'history' ? (
            <div className="relative z-[5]">
              {activeHistoryTab === 'worldcup' ? (
                <PredictionHistoryTable
                  account={historyAccount}
                  site={SITE}
                  activeHistoryTab={activeHistoryTab}
                  onScoreTabClick={() => setActiveHistoryTab('score')}
                  onWorldCupTabClick={() => setActiveHistoryTab('worldcup')}
                />
              ) : (
                <HistoryPredictions
                  mode="page"
                  IP={ip || ''}
                  FP={fp || ''}
                  accountProp={historyAccount}
                  activeHistoryTab={activeHistoryTab}
                  onScoreTabClick={() => setActiveHistoryTab('score')}
                  onWorldCupTabClick={() => setActiveHistoryTab('worldcup')}
                />
              )}
            </div>
          ) : activePredictMode === 'score' ? (
            <div className="relative z-[5]">
              <LeagueSelectorBar
                orderedLeagues={orderedLeagues}
                selectedLeagues={selectedLeagues}
                onLeagueClick={handleLeagueClick}
                onScrollLeft={scrollLeft}
                onScrollRight={scrollRight}
                leagueListRef={leagueListRef}
              />

              <MatchFilterTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onOpenSchedule={() => setShowSchedulePopup(true)}
              />

              <Form
                name="formMatch"
                onFinish={onFinishForm}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                form={form}
                initialValues={{ account: '' }}
              >
                {/* <HistorySearchBar isLoadingSubmit={isLoadingSubmit} /> */}
                <ScorePredictionBoard
                  isLoading={isLoading}
                  filteredMatches={votableMatches}
                  serverNow={serverNow}
                  sectionLeagueTitle={sectionLeagueTitle}
                  titleBg={titleBg}
                  isLoadingSubmit={isLoadingSubmit}
                  getLeagueLogo={getLeagueLogo}
                  formatRemainingClock={formatRemainingClock}
                  onScorePredictClick={() => {
                    setActiveHomeView('predict');
                    setActivePredictMode('score');
                  }}
                />
              </Form>
            </div>
          ) : (
            <div
              id="prediction-result-section"
              className="relative mt-4 md:mt-5 min-h-[560px] max-[1080px]:min-h-0"
            >
              <div className="relative z-[5]">
                <ResultPredictionBoard
                  activeTabIndex={resultActiveTabIndex}
                  onTabIndexChange={setResultActiveTabIndex}
                />
                <ResultRulePrizePanel
                  activeTabIndex={resultActiveTabIndex}
                  onTabIndexChange={setResultActiveTabIndex}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <SchedulePopup open={showSchedulePopup} onClose={() => setShowSchedulePopup(false)} />
    </div>
  );
};

export default QQ88Page;


